// components/hr/TrainingEditModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Option,
  Textarea,
  Button,
  Alert,
  Card,
  CircularProgress,
  Switch,
  Checkbox,
  Chip,
  IconButton,
  Radio,
  RadioGroup,
  Link,
} from '@mui/joy';
import {
  CheckCircle as CheckCircleOutlined,
  Plus as AddOutlined,
  X as CloseOutlined,
  Trash as DeleteOutlined,
  ArrowUp as ArrowUpOutlined,
  ArrowDown as ArrowDownOutlined,
  FileArrowUp as UploadFileOutlined,
  Image as ImageOutlined,
  PlayCircle as PlayCircleOutlined,
  FilePdf as PictureAsPdfOutlined,
  File as InsertDriveFileOutlined,
  ArrowSquareOut as OpenInNewOutlined,
} from '@phosphor-icons/react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { CourseRevisionTools } from './TrainingAdminWorkspace';
import { DraftTrainingPreview } from './TrainingPreviewModal';
import { useTrainingItem, useUpdateTrainingItem, useHrSettings } from '../../hooks/useHr';
import type { TrainingContentType, TrainingLibraryItem } from '../../types/hr';

// ---------------------------------------------------------------------------
// Shared training-authoring vocabulary
//
// These live here rather than in types/hr.ts because the library page, the
// preview modal and the compliance wall all need the same lists and the same
// structured_content shapes, and they all already import this modal's file.
// ---------------------------------------------------------------------------

/** Credential types the backend accepts (`TrainingLibraryItem.credential_type`). */
export type CredentialType =
  | 'osha_10'
  | 'osha_30'
  | 'fall_protection'
  | 'ladder_safety'
  | 'driver_training'
  | 'first_aid_cpr'
  | 'hazard_communication'
  | 'heat_illness'
  | 'equipment'
  | 'other';

export const CREDENTIAL_TYPE_LABELS: Record<CredentialType, string> = {
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  fall_protection: 'Fall Protection',
  ladder_safety: 'Ladder Safety',
  driver_training: 'Driver Training',
  first_aid_cpr: 'First Aid / CPR',
  hazard_communication: 'Hazard Communication',
  heat_illness: 'Heat Illness Prevention',
  equipment: 'Equipment / Tool Certification',
  other: 'Other Credential',
};

export const CREDENTIAL_TYPE_OPTIONS = Object.entries(CREDENTIAL_TYPE_LABELS) as [
  CredentialType,
  string,
][];

/**
 * Content types that can actually be authored here and played back by a
 * learner. Presentation and SCORM are model choices with neither an editor nor
 * a player, so offering them in the picker only produces items nobody can
 * open — they stay hidden unless the `training_scorm_enabled` flag is on.
 */
export const AUTHORABLE_CONTENT_TYPES: TrainingContentType[] = [
  'video',
  'pdf',
  'article',
  'link',
  'checklist',
  'quiz',
];

export const UNPLAYABLE_CONTENT_TYPES: TrainingContentType[] = ['presentation', 'scorm'];

export const CONTENT_TYPE_LABELS: Record<TrainingContentType, string> = {
  video: 'Video',
  pdf: 'PDF Document',
  article: 'Article',
  link: 'External Link',
  checklist: 'Checklist',
  quiz: 'Quiz',
  presentation: 'Presentation',
  scorm: 'SCORM Package',
};

/** Content types the picker offers, given whether unplayable types are unlocked. */
export const contentTypeOptions = (includeUnplayable: boolean): TrainingContentType[] =>
  includeUnplayable
    ? [...AUTHORABLE_CONTENT_TYPES, ...UNPLAYABLE_CONTENT_TYPES]
    : AUTHORABLE_CONTENT_TYPES;

// ---------------------------------------------------------------------------
// structured_content shapes
// ---------------------------------------------------------------------------

export type QuizQuestionType = 'multiple_choice' | 'true_false';

export interface QuizQuestion {
  /** Stable key the learner's answers are submitted under. */
  id: string;
  question: string;
  type: QuizQuestionType;
  options: string[];
  /**
   * The correct option's *text*, not its index.
   *
   * The model documents a string, and the learner API hands out questions
   * stripped to `{id, question, type, options}` — bare strings with no ids — so
   * the text is the only handle a learner can send back. Scoring compares
   * `answers[id] == correct_answer`, so the text round-trips exactly. An index
   * would keep pointing at position N after this editor reorders or deletes an
   * option, silently marking the wrong choice correct.
   */
  correct_answer: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  required: boolean;
}

const TRUE_FALSE_OPTIONS = ['True', 'False'];

const newEntryId = () =>
  `q${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** Read stored quiz questions, tolerating rows written before this editor existed. */
export const normalizeQuizQuestions = (raw: unknown): QuizQuestion[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const source = (entry ?? {}) as Record<string, unknown>;
    const type: QuizQuestionType = source.type === 'true_false' ? 'true_false' : 'multiple_choice';
    const options =
      type === 'true_false'
        ? [...TRUE_FALSE_OPTIONS]
        : Array.isArray(source.options)
        ? source.options.map((option) => String(option))
        : [];
    // Legacy rows (and the old preview modal) treated correct_answer as an
    // option index; resolve it to the option's text on the way in.
    const stored = source.correct_answer;
    const correct =
      typeof stored === 'number'
        ? options[stored] ?? ''
        : stored === undefined || stored === null
        ? ''
        : String(stored);

    return {
      id: source.id ? String(source.id) : newEntryId(),
      question: typeof source.question === 'string' ? source.question : '',
      type,
      options,
      correct_answer: correct,
    };
  });
};

/** Read stored checklist items. The model documents `text`; older rows used `item`. */
export const normalizeChecklistItems = (raw: unknown): ChecklistItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const source = (entry ?? {}) as Record<string, unknown>;
    const text =
      typeof source.text === 'string'
        ? source.text
        : typeof source.item === 'string'
        ? source.item
        : '';
    return {
      id: source.id ? String(source.id) : newEntryId(),
      text,
      required: source.required !== false,
    };
  });
};

/**
 * Why this structured_content cannot be saved, or null when it can.
 *
 * Mirrors the serializer, which rejects a quiz with no question carrying both
 * an id and a correct_answer — an empty quiz used to score 100 and pass
 * everybody. Checking here keeps the message next to the field that is wrong.
 */
export const validateStructuredContent = (
  contentType: TrainingContentType,
  entries: unknown[]
): string | null => {
  if (contentType === 'quiz') {
    const questions = normalizeQuizQuestions(entries);
    if (questions.length === 0) {
      return 'A quiz needs at least one question.';
    }
    for (let index = 0; index < questions.length; index += 1) {
      const question = questions[index];
      if (!question.question.trim()) {
        return `Question ${index + 1} needs question text.`;
      }
      const filled = question.options.filter((option) => option.trim());
      if (filled.length < 2) {
        return `Question ${index + 1} needs at least two answer choices.`;
      }
      if (!question.correct_answer || !filled.includes(question.correct_answer)) {
        return `Question ${index + 1} needs a correct answer selected.`;
      }
    }
    return null;
  }

  if (contentType === 'checklist') {
    const items = normalizeChecklistItems(entries);
    if (items.length === 0) {
      return 'A checklist needs at least one item.';
    }
    if (items.some((item) => !item.text.trim())) {
      return 'Every checklist item needs text.';
    }
  }

  return null;
};

/** Pull a readable message out of a DRF error response. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// StructuredContentEditor
// ---------------------------------------------------------------------------

interface StructuredContentEditorProps {
  contentType: 'quiz' | 'checklist';
  value: unknown[];
  onChange: (next: unknown[]) => void;
}

const move = <T,>(list: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [entry] = next.splice(from, 1);
  next.splice(to, 0, entry);
  return next;
};

/**
 * Authoring for quiz questions and checklist items.
 *
 * Both live in TrainingLibraryItem.structured_content, which had no editor
 * anywhere — so a "Ladder Safety Quiz" could be saved with zero questions.
 */
export const StructuredContentEditor: React.FC<StructuredContentEditorProps> = ({
  contentType,
  value,
  onChange,
}) => {
  if (contentType === 'checklist') {
    const items = normalizeChecklistItems(value);
    // `text` is canonical (it is what the model documents), but the learner's
    // checklist player reads `item`, so write both until it reads `text`.
    const emit = (next: ChecklistItem[]) =>
      onChange(next.map((entry) => ({ ...entry, item: entry.text })));
    const update = (index: number, patch: Partial<ChecklistItem>) =>
      emit(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.length === 0 && (
          <Typography level="body-sm" color="neutral">
            No checklist items yet. Add the steps a crew member has to tick off.
          </Typography>
        )}

        {items.map((item, index) => (
          <Card key={item.id} variant="outlined" sx={{ p: 1.5, gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography level="body-sm" color="neutral" sx={{ pt: 1, minWidth: 20 }}>
                {index + 1}.
              </Typography>
              <Input
                size="sm"
                value={item.text}
                onChange={(e) => update(index, { text: e.target.value })}
                placeholder="e.g. Inspect harness webbing for cuts or fraying"
                sx={{ flex: 1 }}
              />
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                disabled={index === 0}
                onClick={() => emit(move(items, index, index - 1))}
                aria-label="Move item up"
              >
                <ArrowUpOutlined />
              </IconButton>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                disabled={index === items.length - 1}
                onClick={() => emit(move(items, index, index + 1))}
                aria-label="Move item down"
              >
                <ArrowDownOutlined />
              </IconButton>
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={() => emit(items.filter((_, i) => i !== index))}
                aria-label="Delete item"
              >
                <DeleteOutlined />
              </IconButton>
            </Box>
            <Checkbox
              size="sm"
              label="Required"
              checked={item.required}
              onChange={(e) => update(index, { required: e.target.checked })}
              sx={{ ml: 3.5 }}
            />
          </Card>
        ))}

        <Button
          size="sm"
          variant="soft"
          startDecorator={<AddOutlined />}
          onClick={() =>
            emit([...items, { id: newEntryId(), text: '', required: true }])
          }
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Item
        </Button>
      </Box>
    );
  }

  const questions = normalizeQuizQuestions(value);
  const update = (index: number, patch: Partial<QuizQuestion>) =>
    onChange(questions.map((question, i) => (i === index ? { ...question, ...patch } : question)));

  const setType = (index: number, type: QuizQuestionType) => {
    const question = questions[index];
    if (type === 'true_false') {
      update(index, {
        type,
        options: [...TRUE_FALSE_OPTIONS],
        correct_answer: TRUE_FALSE_OPTIONS.includes(question.correct_answer)
          ? question.correct_answer
          : '',
      });
      return;
    }
    update(index, { type, options: question.options.length ? question.options : ['', ''] });
  };

  const setOption = (index: number, optionIndex: number, text: string) => {
    const question = questions[index];
    const previous = question.options[optionIndex];
    const options = question.options.map((option, i) => (i === optionIndex ? text : option));
    // Renaming the option that is marked correct has to carry the answer with
    // it, otherwise correct_answer quietly stops matching any choice.
    const correct_answer =
      question.correct_answer === previous ? text : question.correct_answer;
    update(index, { options, correct_answer });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {questions.length === 0 && (
        <Typography level="body-sm" color="neutral">
          No questions yet. A quiz cannot be saved until it has at least one
          question with a correct answer.
        </Typography>
      )}

      {questions.map((question, index) => (
        <Card key={question.id} variant="outlined" sx={{ p: 1.5, gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="title-sm" sx={{ flex: 1 }}>
              Question {index + 1}
            </Typography>
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              disabled={index === 0}
              onClick={() => onChange(move(questions, index, index - 1))}
              aria-label="Move question up"
            >
              <ArrowUpOutlined />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              disabled={index === questions.length - 1}
              onClick={() => onChange(move(questions, index, index + 1))}
              aria-label="Move question down"
            >
              <ArrowDownOutlined />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => onChange(questions.filter((_, i) => i !== index))}
              aria-label="Delete question"
            >
              <DeleteOutlined />
            </IconButton>
          </Box>

          <Textarea
            size="sm"
            minRows={2}
            value={question.question}
            onChange={(e) => update(index, { question: e.target.value })}
            placeholder="e.g. At what height does OSHA require fall protection on a residential roof?"
          />

          <FormControl size="sm">
            <FormLabel>Answer Type</FormLabel>
            <Select
              size="sm"
              value={question.type}
              onChange={(_, next) => next && setType(index, next as QuizQuestionType)}
            >
              <Option value="multiple_choice">Multiple choice</Option>
              <Option value="true_false">True / False</Option>
            </Select>
          </FormControl>

          <FormControl size="sm">
            <FormLabel>Answer Choices</FormLabel>
            <RadioGroup
              value={question.correct_answer}
              onChange={(e) => update(index, { correct_answer: e.target.value })}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {question.options.map((option, optionIndex) => (
                  <Box
                    key={optionIndex}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Radio
                      size="sm"
                      value={option}
                      disabled={!option.trim()}
                      slotProps={{ input: { 'aria-label': `Mark choice ${optionIndex + 1} correct` } }}
                    />
                    {question.type === 'true_false' ? (
                      <Typography level="body-sm" sx={{ flex: 1 }}>
                        {option}
                      </Typography>
                    ) : (
                      <>
                        <Input
                          size="sm"
                          value={option}
                          onChange={(e) => setOption(index, optionIndex, e.target.value)}
                          placeholder={`Choice ${optionIndex + 1}`}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          disabled={question.options.length <= 2}
                          onClick={() =>
                            update(index, {
                              options: question.options.filter((_, i) => i !== optionIndex),
                              correct_answer:
                                question.correct_answer === option
                                  ? ''
                                  : question.correct_answer,
                            })
                          }
                          aria-label="Delete choice"
                        >
                          <CloseOutlined />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </RadioGroup>
            {question.type === 'multiple_choice' && (
              <Button
                size="sm"
                variant="plain"
                startDecorator={<AddOutlined />}
                onClick={() => update(index, { options: [...question.options, ''] })}
                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              >
                Add Choice
              </Button>
            )}
            <FormHelperText>Select the radio next to the correct choice.</FormHelperText>
          </FormControl>
        </Card>
      ))}

      <Button
        size="sm"
        variant="soft"
        startDecorator={<AddOutlined />}
        onClick={() =>
          onChange([
            ...questions,
            {
              id: newEntryId(),
              question: '',
              type: 'multiple_choice' as QuizQuestionType,
              options: ['', ''],
              correct_answer: '',
            },
          ])
        }
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Question
      </Button>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// TrainingEditModal
// ---------------------------------------------------------------------------

/**
 * The edit payload. credential_type and validity_months are not on
 * TrainingLibraryItem in types/hr.ts yet; the API reads and writes both, and
 * they are what puts a column on the compliance wall.
 */
export type TrainingItemFormData = Omit<Partial<TrainingLibraryItem>, 'file' | 'thumbnail'> & {
  clear_file?: boolean;
  revision_policy?: 'future_only' | 'open_assignments' | 'all_assignments';
  file?: File;
  thumbnail?: File;
  credential_type?: CredentialType | '';
  /** Months a completion stays valid. '' clears it back to "never expires". */
  validity_months?: number | '';
};

interface TrainingEditModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: string | null;
  onSuccess?: () => void;
}

export const TrainingEditModal: React.FC<TrainingEditModalProps> = ({
  open,
  onClose,
  trainingId,
  onSuccess,
}) => {
  const { data: item, isLoading } = useTrainingItem(trainingId || '');
  const { data: hrSettings } = useHrSettings();
  const updateItem = useUpdateTrainingItem();
  const showUnplayableTypes = useFeatureFlag('training_scorm_enabled');

  const [error, setError] = useState<string>('');
  const [newTag, setNewTag] = useState('');

  const [formData, setFormData] = useState<TrainingItemFormData>({});
  const [videoSourceType, setVideoSourceType] = useState<'upload' | 'url'>('upload');

  // Load existing item data
  useEffect(() => {
    if (item) {
      const stored = item as TrainingLibraryItem & {
        credential_type?: CredentialType | '';
        validity_months?: number | null;
      };
      // Normalize on the way in, not just for display: a legacy question with
      // no id is unscoreable, so the API rejects it. Minting ids here means
      // opening and saving an old quiz repairs it.
      const structured =
        item.content_type === 'quiz'
          ? normalizeQuizQuestions(item.structured_content)
          : item.content_type === 'checklist'
          ? normalizeChecklistItems(item.structured_content)
          : item.structured_content || [];
      setFormData({
        title: item.title,
        description: item.description,
        content_type: item.content_type,
        external_url: item.external_url,
        article_content: item.article_content,
        captions_url: item.captions_url, transcript: item.transcript,
        structured_content: structured,
        estimated_duration_minutes: item.estimated_duration_minutes,
        passing_score: item.passing_score,
        requires_completion_confirmation: item.requires_completion_confirmation,
        category: item.category,
        tags: item.tags,
        credential_type: stored.credential_type || '',
        validity_months: stored.validity_months ?? '',
        is_active: item.is_active,
        is_required_for_onboarding: item.is_required_for_onboarding,
      });
      // Set video source type based on existing data
      if (item.content_type === 'video') {
        setVideoSourceType(item.external_url ? 'url' : 'upload');
      }
      setError('');
    }
  }, [item]);

  const contentType = formData.content_type || 'video';
  const structuredContent = (formData.structured_content as unknown[]) || [];

  const handleSubmit = async () => {
    if (!trainingId) return;
    if (!formData.title?.trim()) {
      setError('Title is required');
      return;
    }

    const structuredError = validateStructuredContent(contentType, structuredContent);
    if (structuredError) {
      setError(structuredError);
      return;
    }

    // Only send structured_content for the types that use it, so switching a
    // quiz to a video does not ship orphaned questions back to the API.
    const payload: TrainingItemFormData = { ...formData };
    if (contentType !== 'quiz' && contentType !== 'checklist') {
      delete payload.structured_content;
    }

    try {
      await updateItem.mutateAsync({ id: trainingId, data: payload });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update training item. Please try again.'));
    }
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      const currentTags = formData.tags || [];
      if (!currentTags.includes(newTag.trim())) {
        setFormData({ ...formData, tags: [...currentTags, newTag.trim()] });
      }
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = formData.tags || [];
    setFormData({ ...formData, tags: currentTags.filter((tag) => tag !== tagToRemove) });
  };

  if (!trainingId) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 600,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <ModalClose />
        <DialogTitle>Edit Training Item</DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !item ? (
            <Alert color="danger">Failed to load training item</Alert>
          ) : (
            <>
              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Basic Info */}
                <FormControl required>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={formData.title || ''}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Training title"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the training"
                    minRows={2}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Content Type</FormLabel>
                  <Select
                    value={contentType}
                    onChange={(_, value) =>
                      setFormData({ ...formData, content_type: value as TrainingContentType })
                    }
                  >
                    {contentTypeOptions(
                      showUnplayableTypes || UNPLAYABLE_CONTENT_TYPES.includes(contentType)
                    ).map((type) => (
                      <Option key={type} value={type}>
                        {CONTENT_TYPE_LABELS[type]}
                      </Option>
                    ))}
                  </Select>
                  {UNPLAYABLE_CONTENT_TYPES.includes(contentType) && (
                    <FormHelperText>
                      This type has no player yet — team members cannot open it. Switch it
                      to a video, PDF or article.
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Content Type Specific Fields */}

                {/* Video type - Upload or URL options */}
                {contentType === 'video' && (
                  <>
                    <FormControl>
                      <FormLabel>Video Source</FormLabel>
                      <RadioGroup
                        orientation="horizontal"
                        value={videoSourceType}
                        onChange={(e) => {
                          setVideoSourceType(e.target.value as 'upload' | 'url');
                          setFormData({ ...formData, file: undefined, external_url: '', clear_file: e.target.value === 'url' });
                        }}
                      >
                        <Radio value="upload" label="Upload File" />
                        <Radio value="url" label="YouTube/Vimeo URL" />
                      </RadioGroup>
                    </FormControl>

                    {videoSourceType === 'upload' ? (
                      <FormControl>
                        <FormLabel>Video File</FormLabel>
                        {/* Show existing file if present */}
                        {item?.file && !formData.file && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                              p: 1,
                              bgcolor: 'neutral.100',
                              borderRadius: 'sm',
                            }}
                          >
                            <InsertDriveFileOutlined color="var(--joy-palette-primary-500)" />
                            <Typography level="body-sm" sx={{ flex: 1 }}>
                              Current file uploaded
                            </Typography>
                            <Link
                              href={item.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              startDecorator={<OpenInNewOutlined />}
                              fontSize="sm"
                            >
                              View
                            </Link>
                          </Box>
                        )}
                        <Box
                          sx={{
                            border: '2px dashed',
                            borderColor: formData.file ? 'primary.400' : 'neutral.300',
                            borderRadius: 'sm',
                            p: 2,
                            textAlign: 'center',
                            bgcolor: formData.file ? 'primary.50' : 'transparent',
                          }}
                        >
                          {formData.file ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <PlayCircleOutlined color="var(--joy-palette-primary-500)" />
                              <Typography level="body-sm">{formData.file.name}</Typography>
                              <IconButton
                                size="sm"
                                variant="plain"
                                onClick={() => setFormData({ ...formData, file: undefined })}
                              >
                                <CloseOutlined />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="soft"
                              startDecorator={<UploadFileOutlined />}
                            >
                              {item?.file ? 'Replace Video File' : 'Choose Video File'}
                              <input
                                type="file"
                                hidden
                                accept="video/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) setFormData({ ...formData, file });
                                }}
                              />
                            </Button>
                          )}
                        </Box>
                        <FormHelperText>Supports MP4, WebM, MOV formats</FormHelperText>
                      </FormControl>
                    ) : (
                      <FormControl>
                        <FormLabel>YouTube/Vimeo URL</FormLabel>
                        <Input
                          value={formData.external_url || ''}
                          onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                        />
                        <FormHelperText>Paste a YouTube or Vimeo video link</FormHelperText>
                      </FormControl>
                    )}
                  </>
                )}

                {/* PDF type - File upload */}
                {contentType === 'pdf' && (
                  <FormControl>
                    <FormLabel>PDF File</FormLabel>
                    {/* Show existing file if present */}
                    {item?.file && !formData.file && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          p: 1,
                          bgcolor: 'neutral.100',
                          borderRadius: 'sm',
                        }}
                      >
                        <PictureAsPdfOutlined color="var(--joy-palette-danger-500)" />
                        <Typography level="body-sm" sx={{ flex: 1 }}>
                          Current PDF uploaded
                        </Typography>
                        <Link
                          href={item.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          startDecorator={<OpenInNewOutlined />}
                          fontSize="sm"
                        >
                          View
                        </Link>
                      </Box>
                    )}
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: formData.file ? 'primary.400' : 'neutral.300',
                        borderRadius: 'sm',
                        p: 2,
                        textAlign: 'center',
                        bgcolor: formData.file ? 'primary.50' : 'transparent',
                      }}
                    >
                      {formData.file ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PictureAsPdfOutlined color="var(--joy-palette-danger-500)" />
                          <Typography level="body-sm">{formData.file.name}</Typography>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => setFormData({ ...formData, file: undefined })}
                          >
                            <CloseOutlined />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          component="label"
                          variant="soft"
                          startDecorator={<UploadFileOutlined />}
                        >
                          {item?.file ? 'Replace PDF File' : 'Choose PDF File'}
                          <input
                            type="file"
                            hidden
                            accept=".pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setFormData({ ...formData, file });
                            }}
                          />
                        </Button>
                      )}
                    </Box>
                  </FormControl>
                )}

                {contentType === 'link' && (
                  <FormControl>
                    <FormLabel>External URL</FormLabel>
                    <Input
                      value={formData.external_url || ''}
                      onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </FormControl>
                )}

                {contentType === 'article' && (
                  <FormControl>
                    <FormLabel>Article Content</FormLabel>
                    <Textarea
                      value={formData.article_content || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, article_content: e.target.value })
                      }
                      placeholder="Write your article content here..."
                      minRows={6}
                    />
                  </FormControl>
                )}

                {/* Checklist / Quiz authoring */}
                {(contentType === 'checklist' || contentType === 'quiz') && (
                  <FormControl>
                    <FormLabel>{contentType === 'quiz' ? 'Questions' : 'Checklist Items'}</FormLabel>
                    <StructuredContentEditor
                      contentType={contentType}
                      value={structuredContent}
                      onChange={(next) => setFormData({ ...formData, structured_content: next })}
                    />
                  </FormControl>
                )}

                {contentType === 'video' && <><FormControl><FormLabel>Captions URL (WebVTT)</FormLabel><Input value={formData.captions_url || ''} onChange={e => setFormData({ ...formData, captions_url: e.target.value })} placeholder="https://…/captions.vtt" /></FormControl><FormControl><FormLabel>Video transcript</FormLabel><Textarea value={formData.transcript || ''} onChange={e => setFormData({ ...formData, transcript: e.target.value })} minRows={4} /></FormControl></>}
                {/* Thumbnail picker */}
                <FormControl>
                  <FormLabel>Thumbnail (Optional)</FormLabel>
                  {/* Show existing thumbnail if present */}
                  {item?.thumbnail && !formData.thumbnail && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        p: 1,
                        bgcolor: 'neutral.100',
                        borderRadius: 'sm',
                      }}
                    >
                      <Box
                        component="img"
                        src={item.thumbnail}
                        alt="Current thumbnail"
                        sx={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 'xs' }}
                      />
                      <Typography level="body-sm" sx={{ flex: 1 }}>
                        Current thumbnail
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: formData.thumbnail ? 'primary.400' : 'neutral.300',
                      borderRadius: 'sm',
                      p: 2,
                      textAlign: 'center',
                      bgcolor: formData.thumbnail ? 'primary.50' : 'transparent',
                    }}
                  >
                    {formData.thumbnail ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <ImageOutlined color="var(--joy-palette-primary-500)" />
                        <Typography level="body-sm">{formData.thumbnail.name}</Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => setFormData({ ...formData, thumbnail: undefined })}
                        >
                          <CloseOutlined />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        component="label"
                        variant="soft"
                        color="neutral"
                        size="sm"
                        startDecorator={<ImageOutlined />}
                      >
                        {item?.thumbnail ? 'Replace Thumbnail' : 'Add Thumbnail'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setFormData({ ...formData, thumbnail: file });
                          }}
                        />
                      </Button>
                    )}
                  </Box>
                  <FormHelperText>Optional cover image for the training item</FormHelperText>
                </FormControl>

                {/* Category and Duration */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Category</FormLabel>
                    <Input
                      value={formData.category || ''}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Safety, Sales, etc."
                    />
                  </FormControl>
                  <FormControl sx={{ width: 140 }}>
                    <FormLabel>Duration (min)</FormLabel>
                    <Input
                      type="number"
                      value={formData.estimated_duration_minutes || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          estimated_duration_minutes: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </FormControl>
                </Box>

                {/* Credential — what puts this item on the compliance wall */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Safety Credential</FormLabel>
                    <Select
                      value={formData.credential_type || ''}
                      onChange={(_, value) =>
                        setFormData({ ...formData, credential_type: (value as CredentialType) || '' })
                      }
                    >
                      <Option value="">Not a credential</Option>
                      {CREDENTIAL_TYPE_OPTIONS.map(([value, label]) => (
                        <Option key={value} value={value}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Tagged items become a column on the compliance wall.
                    </FormHelperText>
                  </FormControl>
                  <FormControl sx={{ width: 160 }}>
                    <FormLabel>Valid For (months)</FormLabel>
                    <Input
                      type="number"
                      value={formData.validity_months ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          validity_months: e.target.value === '' ? '' : parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder="Never expires"
                      slotProps={{ input: { min: 1, max: 120 } }}
                    />
                    <FormHelperText>Blank never expires</FormHelperText>
                  </FormControl>
                </Box>

                {/* Quiz Specific */}
                {contentType === 'quiz' && (
                  <FormControl sx={{ width: 200 }}>
                    <FormLabel>Passing Score (%)</FormLabel>
                    <Input
                      type="number"
                      value={formData.passing_score ?? hrSettings?.quiz_passing_score ?? 70}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          passing_score: parseInt(e.target.value) || 0,
                        })
                      }
                      slotProps={{ input: { min: 0, max: 100 } }}
                    />
                  </FormControl>
                )}

                {/* Tags */}
                <FormControl>
                  <FormLabel>Tags</FormLabel>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {(formData.tags || []).map((tag) => (
                      <Chip
                        key={tag}
                        size="sm"
                        variant="soft"
                        endDecorator={
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => handleRemoveTag(tag)}
                          >
                            <CloseOutlined size={14} />
                          </IconButton>
                        }
                      >
                        {tag}
                      </Chip>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Input
                      size="sm"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      sx={{ flex: 1 }}
                    />
                    <IconButton size="sm" onClick={handleAddTag}>
                      <AddOutlined />
                    </IconButton>
                  </Box>
                </FormControl>

                {/* Toggles */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography level="body-sm">Requires Completion Confirmation</Typography>
                      <Typography level="body-xs" color="neutral">
                        User must confirm they completed the training
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.requires_completion_confirmation || false}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          requires_completion_confirmation: e.target.checked,
                        })
                      }
                    />
                  </Box>

                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography level="body-sm">Required for Onboarding</Typography>
                      <Typography level="body-xs" color="neutral">
                        New hires must complete during onboarding
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_required_for_onboarding || false}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_required_for_onboarding: e.target.checked,
                        })
                      }
                    />
                  </Box>

                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography level="body-sm">Active</Typography>
                      <Typography level="body-xs" color="neutral">
                        Item is visible in the library
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_active !== false}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        {item && <CourseRevisionTools id={item.id} policy={formData.revision_policy || 'future_only'} onPolicy={revision_policy => setFormData({ ...formData, revision_policy })} />}
        <DraftTrainingPreview draft={formData} saved={item} />
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={updateItem.isPending}
            startDecorator={<CheckCircleOutlined />}
            disabled={isLoading || !item}
          >
            Save Changes
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default TrainingEditModal;
